import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { getPlantDetails, getSheetColumns } from "../../../network/cipAPI";
import {
  storeFileId,
  storeDefaultInformation,
  storePlantDetails,
  storeTrainChemicalSelection,
  updateSelectedTrain,
  storePlantData,
} from "../../../Store/TemplateStore/template.action";
import createNotification, {
  ERROR_NOTIFICATION,
} from "../../../components/Notifications";
import { useUnsavedChanges } from "../../../hooks/useUnsavedChanges";
import { getDatasetDetails, updatePlantDataTrain } from "../../../utils/Utils";
import WPTabs from "../../../packages/WPTabs";
import { CorrectIcon, WrongIcon } from "../../../assets/icons";
import IconButton from "../../../packages/IconButton";
import { DATA_APPEND_SECTION_ID } from "../../../constants/global.constants";

import "./TrainTabsSection.scss";

const TrainTabsSection = ({
  config,
  selectedTrain,
  hasUnsavedChanges,
  trainReadinessMap,
  plantId,
  tempSelectedTrain,
  trainTabActions,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { plantData } = useSelector((state) => state);

  const { setShowDialog, allowActionRef, pendingActionRef } =
    useUnsavedChanges();
  const {
    setIsLoading,
    setFileId,
    setMappedColumn,
    setMappedReference,
    setSelectedTrain,
    setSelectedSheet,
    setSelectedCIPFileName,
    setHasRecurrentCIPs,
    setIsAppendFile,
    setAppendFileList,
    setAppendStatus,
    setNormSelectionType,
    setDatasetDetails,
    setSheetNames,
    setColumnOptions,
    setFileInfo,
    setUploadedFiles,
    setColMappingType,
    setTempSelectedTrain,
  } = trainTabActions;

  const trainOptions = useMemo(() => {
    const trainCount = config?.trains?.length || 0;
    return Array.from({ length: trainCount }, (_, idx) => String(idx + 1));
  }, [config?.trains?.length]);

  const trainTabs = useMemo(
    () =>
      trainOptions.map((train) => ({
        id: `analyse-data-train-tab-${train}`,
        train,
        title: (
          <div className="analyse-data-train-tab-title">
            <span className="analyse-data-train-tab-label">
              {t("ValueStrings.train")} {train}
            </span>
            <IconButton
              tooltip={
                trainReadinessMap && trainReadinessMap[Number(train)]
                  ? t("analyseData.trainConfigurationCompleted")
                  : t("analyseData.trainConfigurationIncomplete")
              }
              className="analyse-data-train-tab-status-icon"
              stopPropagation={false}
            >
              {trainReadinessMap && trainReadinessMap[Number(train)] ? (
                <CorrectIcon />
              ) : (
                <WrongIcon />
              )}
            </IconButton>
          </div>
        ),
      })),
    [t, trainOptions, trainReadinessMap],
  );

  const activeTrainTabIndex = useMemo(() => {
    const selectedIndex = trainTabs.findIndex(
      (tab) => Number(tab.train) === Number(selectedTrain),
    );

    return selectedIndex >= 0 ? selectedIndex : 0;
  }, [trainTabs, selectedTrain]);

  const resetStateForTrainSwitch = () => {
    setFileId(null);
    setMappedColumn([]);
    setMappedReference(null);
  };

  const scrollToAppendSection = () => {
    const appendSectionElement = document.getElementById(
      DATA_APPEND_SECTION_ID,
    );
    if (!appendSectionElement) return;

    appendSectionElement.style.scrollMarginTop = "38px";
    appendSectionElement.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleAppendFlowCompletion = () => {
    setShowDialog(false);
    scrollToAppendSection();
    setTempSelectedTrain(null);
  };

  const handleTrainOptionChange = async (value) => {
    if (Number(value) === Number(selectedTrain)) {
      if (tempSelectedTrain) {
        handleAppendFlowCompletion();
      }
      return;
    }
    if (hasUnsavedChanges && allowActionRef.current === false) {
      setShowDialog(true);
      pendingActionRef.current = () => handleTrainOptionChange(value);
      return;
    }

    setFileInfo([]);
    setSelectedSheet(null);
    setSelectedCIPFileName(null);
    setHasRecurrentCIPs(false);
    setIsAppendFile(false);
    setAppendFileList([]);

    if (!value) {
      resetStateForTrainSwitch();
      setSelectedTrain(null);
      setSelectedSheet(null);
      return;
    }

    setIsLoading(true);

    try {
      const response = await getPlantDetails(plantId, true, value);
      if (response && response.data) {
        const {
          file,
          parent_file_name,
          plant_file_id,
          default_cip,
          col_mapping,
          sel_train,
          bulk_cip_file_name,
          has_recurrent_cips,
          owner_username,
          name,
          water_type,
          sheet_names,
          ref_condition,
          isAppended,
          append_files,
          norm_selection_type,
          chemical_selection,
          uploaded_files,
          col_mapping_type,
        } = response.data;

        dispatch(
          storePlantData(updatePlantDataTrain(plantData, plantId, sel_train)),
        );

        let file_id, sheet_name, min_date, max_date, uploaded_in_blob;
        if (file) {
          ({ file_id, sheet_name, min_date, max_date, uploaded_in_blob } =
            file);
        }

        setFileId(file_id);
        const fileInfo = {
          file_id,
          sheetName: sheet_name,
          fileName: parent_file_name,
          plant_file_id,
          uploaded_in_blob,
        };

        dispatch(
          storePlantDetails({
            plantName: name,
            water_type,
          }),
        );
        dispatch(storeFileId(fileInfo));
        setFileInfo(fileInfo);
        if (chemical_selection) {
          dispatch(storeTrainChemicalSelection(chemical_selection));
        }

        if (default_cip) {
          dispatch(storeDefaultInformation(default_cip));
        }

        setMappedColumn(col_mapping);
        setSelectedTrain(sel_train);
        setSelectedCIPFileName(bulk_cip_file_name);
        setHasRecurrentCIPs(has_recurrent_cips);
        setDatasetDetails(
          getDatasetDetails({
            owner_username,
            min_date,
            max_date,
          }),
        );

        setIsAppendFile(isAppended);
        setAppendFileList(append_files);
        setAppendStatus("success");
        setNormSelectionType(norm_selection_type);
        dispatch(updateSelectedTrain(sel_train));
        setSheetNames(sheet_names);
        setSelectedSheet(sheet_name);
        setMappedReference(ref_condition?.[0]);
        setUploadedFiles(uploaded_files);
        setColMappingType(col_mapping_type);

        if (file_id && uploaded_in_blob) {
          setColumnOptions([]);
          const columns = await getSheetColumns(file_id, sel_train);
          if (columns && columns.data) {
            setColumnOptions(columns.data?.cols);
          }
        }
      }

      if (tempSelectedTrain) {
        handleAppendFlowCompletion();
      }
    } catch (error) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
      resetStateForTrainSwitch();
      setSelectedSheet(null);
      setSelectedCIPFileName(null);
      setHasRecurrentCIPs(false);
      setIsAppendFile(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tempSelectedTrain) {
      handleTrainOptionChange(tempSelectedTrain);
    }
  }, [tempSelectedTrain]);

  if (trainTabs.length === 0) return null;

  return (
    <div className="analyse-data-train-tabs-shell">
      <WPTabs
        tabs={trainTabs}
        className="analyse-data-train-tabs"
        activeTabIndex={activeTrainTabIndex}
        onTabChange={(index) =>
          handleTrainOptionChange(trainTabs[index]?.train)
        }
      >
        {trainTabs.map((tab) => (
          <div key={`train-tab-content-${tab.id}`} />
        ))}
      </WPTabs>
    </div>
  );
};

export default TrainTabsSection;
